import { useCallback, useMemo } from 'react';
import { batch } from 'react-redux';
import { v4 as uuid } from 'uuid';

import {
    appsEndpoints,
    useGetLastAppInvocationQuery,
    useInvokeAppMutation,
} from '../../../api/endpoints/apps-v2-endpoint';
import { usageMetricsEndpoint } from '../../../api/endpoints/usage-metrics-endpoint';
import { ActionTypeV2, IInvokeAppV2Params, StreamStatus, UsageMetricsEventType } from '../../../api/types';
import { SHARED_ANSWERS_CACHE_KEY } from '../../../redux/constants';
import { FeatureType, MixpanelEvent } from '../../../services/mixpanel/types';
import { setAnalyticsSent } from '../../slices/answers/answers-slice';
import { setProactiveMeta } from '../../slices/question/question-slice';
import { sendMetrics } from '../../thunks/metrics-thunk';
import { useAppDispatch } from '../app-hooks';
import { useProject } from '../settings-hooks';

type IInvokeAppParams = Pick<IInvokeAppV2Params, 'query' | 'url' | 'app'>;

/**
 * Custom hook for managing app invocations (AppsV2).
 * Provides functionality to invoke apps and abort ongoing invocations.
 */
export const useAppsV2 = () => {
    const dispatch = useAppDispatch();
    const { project: customer_project_id } = useProject();
    const { data: appInvocation } = useGetLastAppInvocationQuery({ customer_project_id });
    const [invoke, { originalArgs }] = useInvokeAppMutation({ fixedCacheKey: SHARED_ANSWERS_CACHE_KEY });

    /**
     * Aborts the current app invocation if it's running.
     */
    const abortAppInvocation = useCallback(async () => {
        if (appInvocation?.status !== StreamStatus.RUNNING) {
            return; // No action if no app is currently running
        }

        // Abort the invocation if a controller is available
        originalArgs?.controller.abort();

        const meta = {
            conversationId: appInvocation.app_id,
            turnId: appInvocation.invocation_id,
        };

        // Send Mixpanel metrics for app abortion
        dispatch(
            sendMetrics({
                event: MixpanelEvent.APP_V2_ABORT,
                meta,
            })
        );

        // Send usage metrics for app abortion
        dispatch(
            usageMetricsEndpoint.endpoints.sendMetrics.initiate({
                type: UsageMetricsEventType.APP_V2_ABORT,
                meta: {
                    ...meta,
                    output: appInvocation.content,
                    timestamp: Date.now(),
                },
            })
        );
    }, [appInvocation, originalArgs, dispatch]);

    const updateAppInvocation = useCallback(
        (content: string) => {
            dispatch(
                appsEndpoints.util.updateQueryData('getLastAppInvocation', { customer_project_id }, (draft) => {
                    if (!draft) {
                        return;
                    }
                    draft.content = content;
                })
            );
        },
        [dispatch, customer_project_id]
    );

    /**
     * Invokes a new app with the provided parameters.
     * Aborts any currently running app invocation before invoking a new one.
     */
    const invokeApp = useCallback(
        async (params: IInvokeAppParams) => {
            if (appInvocation?.status === StreamStatus.RUNNING) {
                // Abort any currently running app before invoking a new one
                abortAppInvocation();
            }

            batch(() => {
                dispatch(setAnalyticsSent(false));
                // do a cleanup of the proactive meta
                dispatch(setProactiveMeta(null));
            });

            const { app, ...rest } = params;
            const invocation_id = uuid();
            const controller = new AbortController();

            // Invoke the new app
            await invoke({
                ...rest,
                customer_project_id,
                invocation_id,
                controller,
                app,
            });

            // Log to mixpanel
            const additional_meta = {
                action_id: app.action_id,
                title: app.title,
            };

            dispatch(
                sendMetrics({
                    event: MixpanelEvent.AI_HELPER,
                    meta: {
                        question: params.query,
                        action_type: ActionTypeV2.CUSTOM,
                        url: params.url,
                        feature: FeatureType.APPS,
                        question_id: appInvocation?.invocation_id,
                        ...additional_meta,
                    },
                })
            );
        },
        [customer_project_id, appInvocation, invoke, abortAppInvocation, dispatch]
    );

    return useMemo(
        () => ({
            invoke: invokeApp,
            abort: abortAppInvocation,
            update: updateAppInvocation,
            appInvocation,
        }),
        [invokeApp, abortAppInvocation, updateAppInvocation, appInvocation]
    );
};
